import React, { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Fab,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Grid,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  Container,
  Stack,
  Divider,
  Badge,
  Slide,
  useMediaQuery,
  InputAdornment
} from '@mui/material'
import { useTheme } from '@mui/material/styles';
import {
  Send as SendIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Launch as LaunchIcon,
  AccountBalance as CreditIcon
} from '@mui/icons-material'
import { useQuery } from 'react-query'
import { ConfigValue } from '../../Config'
import useAuth from '../../Hooks/use-auth'

const getEmailDomain = email => email?.split('@')[1] || ''

// fetch company users
async function fetchUsersByCompany() {
  const res = await fetch(
    `${ConfigValue.PUBLIC_REST_API_ENDPOINT}/creatorUsers/users_by_company`
  )
  const raw = await res.json()
  const filtered = {}
  Object.keys(raw).forEach(company => {
    const users = raw[company].filter(
      u => u.email && !u.email.endsWith('@gmail.com')
    )
    if (users.length) filtered[company] = users
  })
  return filtered
}

// fetch CRM contacts
async function fetchContacts() {
  const res = await fetch(
    `${ConfigValue.PUBLIC_REST_API_ENDPOINT}/crm/contacts`
  )
  const raw = await res.json()
  return raw.filter(c => c.email && !c.email.endsWith('@gmail.com'))
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

export default function AgencyOutbound() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  
  // get logged-in user id
  const { getCurrrentUser } = useAuth()
  const userId = getCurrrentUser()?.id

  // selection + dialog state
  const [selected, setSelected] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [rate, setRate] = useState('')
  const [ccEmail, setCcEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showRecommendations, setShowRecommendations] = useState(true)

  // CredX credits & sent-count
  const [credits, setCredits] = useState(0)
  const [pitchesSent, setPitchesSent] = useState(0)
  const [error, setError] = useState('')

  // load credits on mount / when userId changes
  useEffect(() => {
    if (!userId) return
    fetch(`${ConfigValue.PUBLIC_REST_API_ENDPOINT}/credx/credits?user_id=${userId}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch credits')
        return res.json()
      })
      .then(data => setCredits(data.credits))
      .catch(err => {
        console.error(err)
        setError('Failed to load credits')
      })
  }, [userId])

  // queries
  const { data: usersByCompany, isLoading: loadingUsers } = useQuery(
    'usersByCompany',
    fetchUsersByCompany
  )
  const { data: contacts, isLoading: loadingContacts } = useQuery(
    'contacts',
    fetchContacts
  )

  const processedUsers = usersByCompany || {}
  const processedContacts = contacts || []
  const allLoaded = !loadingUsers && !loadingContacts

  // build top-10 domain recommendations
  const recommendations = useMemo(() => {
    const domainCounts = {}
    processedContacts.forEach(c => {
      const dom = getEmailDomain(c.email)
      if (!dom) return
      if (!domainCounts[dom]) domainCounts[dom] = { count: 0, contacts: [] }
      domainCounts[dom].count++
      domainCounts[dom].contacts.push(c)
    })
    const sortedDomains = Object.keys(domainCounts).sort(
      (a, b) => domainCounts[b].count - domainCounts[a].count
    )
    return sortedDomains
      .flatMap(d => domainCounts[d].contacts)
      .slice(0, 10)
  }, [processedContacts])

  // Filter data based on search
  const filteredRecommendations = useMemo(() => {
    if (!searchQuery) return recommendations
    return recommendations.filter(c => 
      (c.company_name || getEmailDomain(c.email)).toLowerCase().includes(searchQuery.toLowerCase()) ||
      getEmailDomain(c.email).toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [recommendations, searchQuery])

  const filteredCompanies = useMemo(() => {
    if (!searchQuery) return Object.keys(processedUsers)
    return Object.keys(processedUsers).filter(company =>
      company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getEmailDomain(processedUsers[company][0].email).toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [processedUsers, searchQuery])

  // select / deselect
  const toggle = id =>
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )

  const isSelected = id => selected.includes(id)

  // open dialog only if we have credits & a selection
  const handleOpen = () => {
    if (selected.length === 0) {
      setError('Select at least one contact or company first.')
      return
    }
    if (selected.length > credits) {
      setError('Not enough credits.')
      return
    }
    setError('')
    setDialogOpen(true)
  }

  // send pitches
  const sendPitch = async () => {
    if (!ccEmail) {
      setError('Your own email (CC) is required.')
      return
    }
    setSending(true)
    setError('')
    try {
      // 1. Subtract credits
      const subRes = await fetch(
        `${ConfigValue.PUBLIC_REST_API_ENDPOINT}/credx/subtract_credx`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            email_count: selected.length
          })
        }
      )
      if (!subRes.ok) {
        const { error } = await subRes.json()
        throw new Error(error || 'Failed to subtract credits')
      }
      const { credits: newCredits } = await subRes.json()
      setCredits(newCredits)
      setPitchesSent(ps => ps + selected.length)

      // 2. Send each email
      for (const id of selected) {
        let recipients = []
        // if it's a company key
        if (processedUsers[id]) {
          recipients = processedUsers[id].map(u => u.email)
        } else {
          // else it's an individual contact email
          recipients = [id]
        }
        for (const email of recipients) {
          const mailRes = await fetch(
            `${ConfigValue.PUBLIC_REST_API_ENDPOINT}/credx/send_pitch_email`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_id: userId,
                recipient_email: email,
                subject,
                body: `${body}\n\nRate: ${rate}`,
                cc_email: ccEmail
              })
            }
          )
          if (!mailRes.ok) {
            const { error } = await mailRes.json()
            console.warn(`Failed to send to ${email}:`, error)
          }
        }
      }
      // close & reset
      setDialogOpen(false)
      setSelected([])
      setSubject('')
      setBody('')
      setRate('')
      setCcEmail('')
    } catch (err) {
      setError(`Error: ${err.message}`)
    } finally {
      setSending(false)
    }
  }

  const ContactCard = ({ contact, type = 'contact' }) => {
    const id = type === 'company' ? contact.company : contact.email
    const displayName = type === 'company' ? contact.company : (contact.company_name || getEmailDomain(contact.email))
    const domain = type === 'company' ? getEmailDomain(contact.users[0].email) : getEmailDomain(contact.email)
    const userCount = type === 'company' ? contact.users.length : 1
    
    return (
      <Card 
        sx={{ 
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          transform: isSelected(id) ? 'scale(0.98)' : 'scale(1)',
          borderColor: isSelected(id) ? 'primary.main' : 'divider',
          borderWidth: isSelected(id) ? 2 : 1,
          borderStyle: 'solid',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: theme.shadows[4]
          }
        }}
        onClick={() => toggle(id)}
      >
        <CardContent sx={{ pb: 1 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar
              sx={{ 
                bgcolor: isSelected(id) ? 'primary.main' : 'grey.100',
                color: isSelected(id) ? 'white' : 'grey.600'
              }}
            >
              {type === 'company' ? <BusinessIcon /> : <PersonIcon />}
            </Avatar>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {displayName}
              </Typography>
              
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {domain}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    window.open(`https://${domain}`, '_blank')
                  }}
                >
                  <LaunchIcon fontSize="small" />
                </IconButton>
              </Stack>
              
              {type === 'company' && userCount > 1 && (
                <Chip
                  size="small"
                  label={`${userCount} users`}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isSelected(id) ? (
                <CheckCircleIcon color="primary" />
              ) : (
                <UncheckedIcon color="action" />
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold" sx={{ mb: 2 }}>
          Outbound Pitches
        </Typography>
        
        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                  <EmailIcon color="primary" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {pitchesSent}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pitches Sent
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                  <CreditIcon color="success" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {credits}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Credits Available
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                  <BusinessIcon color="info" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color="info.main">
                      {selected.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Selected
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Search and Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems="center">
              <TextField
                placeholder="Search companies and contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                variant={showRecommendations ? "contained" : "outlined"}
                startIcon={<FilterIcon />}
                onClick={() => setShowRecommendations(!showRecommendations)}
                size="small"
              >
                Show Recommendations
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {!allLoaded ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Stack spacing={4}>
          {/* Recommendations Section */}
          {showRecommendations && (
            <Box>
              <Typography variant="h5" fontWeight="600" sx={{ mb: 2 }}>
                Top Recommendations
              </Typography>
              <Grid container spacing={2}>
                {filteredRecommendations.map(contact => (
                  <Grid item xs={12} sm={6} md={4} key={contact.email}>
                    <ContactCard contact={contact} type="contact" />
                  </Grid>
                ))}
              </Grid>
              {filteredRecommendations.length === 0 && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No recommendations found matching your search.
                </Typography>
              )}
            </Box>
          )}

          {/* Companies Section */}
          <Box>
            <Typography variant="h5" fontWeight="600" sx={{ mb: 2 }}>
              Verified Companies
            </Typography>
            <Grid container spacing={2}>
              {filteredCompanies.map(companyName => (
                <Grid item xs={12} sm={6} md={4} key={companyName}>
                  <ContactCard 
                    contact={{
                      company: companyName,
                      users: processedUsers[companyName]
                    }} 
                    type="company" 
                  />
                </Grid>
              ))}
            </Grid>
            {filteredCompanies.length === 0 && (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No companies found matching your search.
              </Typography>
            )}
          </Box>
        </Stack>
      )}

      {/* Floating Action Button */}
      {selected.length > 0 && (
        <Badge badgeContent={selected.length} color="primary">
          <Fab
            color="primary"
            size="large"
            sx={{ 
              position: 'fixed', 
              bottom: isMobile ? 16 : 24, 
              right: isMobile ? 16 : 24,
              zIndex: 1000
            }}
            onClick={handleOpen}
          >
            <SendIcon />
          </Fab>
        </Badge>
      )}

      {/* Send Pitch Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={Transition}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" fontWeight="600">
            Send Pitch Email
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sending to {selected.length} recipient{selected.length !== 1 ? 's' : ''}
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Email Subject"
              fullWidth
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Enter your email subject..."
            />
            
            <TextField
              label="Email Body"
              fullWidth
              multiline
              rows={6}
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Write your pitch message..."
            />
            
            <TextField
              label="Your Rate"
              fullWidth
              value={rate}
              onChange={e => setRate(e.target.value)}
              placeholder="e.g., $50/hour, $500/project"
            />
            
            <TextField
              label="Your Email (CC)"
              fullWidth
              required
              value={ccEmail}
              onChange={e => setCcEmail(e.target.value)}
              placeholder="your.email@company.com"
              helperText="You'll be copied on all sent emails"
            />
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            disabled={sending}
            size="large"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={sendPitch}
            disabled={sending}
            startIcon={sending ? <CircularProgress size={18} /> : <SendIcon />}
            size="large"
          >
            {sending ? 'Sending...' : 'Send Pitch'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}